import React, { useState, useEffect } from 'react';
import { Link, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { LayoutDashboard, LineChart, Wallet, ArrowDownToLine, ArrowUpToLine, History, Settings, LogOut, Bell, Search, ChevronDown, Radio, BarChart3, Newspaper, BellRing, ShieldCheck, Shield, Gift, Menu, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { adminAPI } from '../../lib/adminAPI';
import NotificationBell from '../../components/NotificationBell';

const NAV = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Overview' },
  { to: '/dashboard/trade', icon: LineChart, label: 'Trade Terminal' },
  { to: '/dashboard/alerts', icon: BellRing, label: 'Price Alerts' },
  { to: '/dashboard/analytics', icon: BarChart3, label: 'Analytics' },
  { to: '/dashboard/news', icon: Newspaper, label: 'News' },
  { to: '/dashboard/portfolio', icon: Wallet, label: 'Portfolio' },
  { to: '/dashboard/deposit', icon: ArrowDownToLine, label: 'Deposit' },
  { to: '/dashboard/withdraw', icon: ArrowUpToLine, label: 'Withdraw' },
  { to: '/dashboard/referral', icon: Gift, label: 'Referral Program' },
  { to: '/dashboard/kyc', icon: ShieldCheck, label: 'KYC Verification' },
  { to: '/dashboard/history', icon: History, label: 'History' },
  { to: '/dashboard/settings', icon: Settings, label: 'Settings' }
];

const DashboardLayout = () => {
  const nav = useNavigate();
  const loc = useLocation();
  const { user, logout, impersonating, exitImpersonation } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    adminAPI.me().then(r => setIsAdmin(!!r.is_admin)).catch(() => setIsAdmin(false));
  }, []);

  // Close drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [loc.pathname]);

  if (!user) return null;

  const handleLogout = () => { logout(); nav('/'); };

  const Sidebar = (
    <>
      <Link to="/" className="flex items-center gap-2 px-6 py-5 border-b border-[#141f1b]">
        <svg width="28" height="28" viewBox="0 0 32 32" fill="none">
          <path d="M6 22 L18 6 L26 12 L14 28 Z" fill="#00e676"/>
          <path d="M14 28 L22 20" stroke="#062516" strokeWidth="2"/>
        </svg>
        <span className="text-lg font-extrabold text-white whitespace-nowrap">NEXUS FX <span className="text-[#00e676]">PRO</span></span>
      </Link>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {NAV.map(n => {
          const active = loc.pathname === n.to;
          return (
            <Link key={n.to} to={n.to} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition ${active ? 'bg-[#00e676]/10 text-[#00e676] border border-[#00e676]/20' : 'text-gray-400 hover:text-white hover:bg-[#0a1210]'}`}>
              <n.icon size={16}/> {n.label}
            </Link>
          );
        })}
        {isAdmin && (
          <Link to="/admin" className="mt-2 flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium border border-purple-500/30 bg-purple-500/5 text-purple-400 hover:bg-purple-500/10">
            <Shield size={16}/> Admin CRM
          </Link>
        )}
      </nav>
      <div className="p-4 border-t border-[#141f1b]">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-full bg-[#00e676]/15 border border-[#00e676]/30 flex items-center justify-center text-[#00e676] font-bold text-sm">
            {user.name?.[0]?.toUpperCase() || 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-white truncate">{user.name}</div>
            <div className="text-xs text-gray-500 truncate">{user.email}</div>
          </div>
        </div>
        <button onClick={handleLogout} className="w-full flex items-center justify-center gap-2 py-2 rounded-lg border border-[#1a2823] text-gray-400 hover:text-red-400 hover:border-red-500/40 text-sm">
          <LogOut size={14}/> Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#050807] flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 bg-[#070c0a] border-r border-[#141f1b] flex-col fixed inset-y-0 z-30">
        {Sidebar}
      </aside>

      {/* Mobile drawer + backdrop */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-50" onClick={() => setDrawerOpen(false)} data-testid="sidebar-backdrop">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"/>
          <aside
            onClick={e => e.stopPropagation()}
            className="absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-[#070c0a] border-r border-[#141f1b] flex flex-col shadow-2xl animate-[slide-in_.25s_ease-out]"
            data-testid="mobile-sidebar"
          >
            {Sidebar}
          </aside>
          <style>{`@keyframes slide-in { from { transform: translateX(-100%);} to { transform: translateX(0);} }`}</style>
        </div>
      )}

      {/* Main */}
      <main className="flex-1 lg:ml-64 min-w-0">
        <header className="sticky top-0 z-30 bg-[#050807]/85 backdrop-blur-xl border-b border-[#141f1b] px-4 sm:px-6 lg:px-8 py-3 lg:py-4 flex items-center justify-between gap-3">
          {/* Mobile hamburger + logo */}
          <div className="flex items-center gap-2 lg:hidden shrink-0">
            <button
              onClick={() => setDrawerOpen(true)}
              data-testid="open-sidebar-btn"
              className="w-9 h-9 rounded-lg border border-[#1a2823] flex items-center justify-center text-gray-300 hover:text-[#00e676] hover:border-[#00e676]"
            >
              <Menu size={16}/>
            </button>
            <Link to="/dashboard" className="flex items-center gap-1.5">
              <svg width="20" height="20" viewBox="0 0 32 32" fill="none">
                <path d="M6 22 L18 6 L26 12 L14 28 Z" fill="#00e676"/>
              </svg>
              <span className="text-sm font-extrabold text-white">NEXUS FX <span className="text-[#00e676]">PRO</span></span>
            </Link>
          </div>

          {/* Search: full-width on desktop, hidden on small screens */}
          <div className="relative hidden md:block flex-1 max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"/>
            <input placeholder="Search markets, symbols, tools..." className="w-full bg-[#0a1210] border border-[#1a2823] rounded-lg pl-9 pr-4 py-2 text-sm text-white focus:outline-none focus:border-[#00e676]"/>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <NotificationBell/>
            <div className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-lg border border-[#1a2823] text-sm text-white">
              <span className="w-2 h-2 rounded-full bg-[#00e676] pulse-dot"/>
              Live Account <ChevronDown size={13} className="text-gray-400"/>
            </div>
            <div className="sm:hidden flex items-center gap-1.5 px-2 py-1.5 rounded-lg border border-[#00e676]/30 text-xs text-[#00e676]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00e676] pulse-dot"/> LIVE
            </div>
          </div>
        </header>
        {impersonating && (
          <div data-testid="impersonation-banner" className="bg-amber-500/15 border-b border-amber-500/30 px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between gap-3">
            <span className="text-xs sm:text-sm text-amber-300 font-semibold">👁 Viewing as <span className="text-white">{user?.name}</span> ({user?.email}) — admin view</span>
            <button onClick={async () => { await exitImpersonation(); nav('/admin/users'); }} data-testid="exit-impersonation-btn" className="text-xs font-bold px-3 py-1.5 rounded bg-amber-500/20 text-amber-200 border border-amber-500/40 hover:bg-amber-500/30 whitespace-nowrap">Exit to admin</button>
          </div>
        )}
        <div className="p-4 sm:p-6 lg:p-8 min-w-0">
          <Outlet/>
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;
